import {useEffect} from 'react';

import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import type {RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {REGEX_METRIC_MAX_LEN} from '../PanelRunsLinePlot/utils';
import {useMetricKeySearch} from './useMetricKeySearch';

type MetricsConfig = {
  metricRegex?: string;
  metrics?: string[];
  useMetricRegex?: boolean;
};

type UpdateConfig = (
  arg: Partial<RunsLinePlotConfig>,
  options?: {isUndoable?: boolean}
) => void;

const EMPTY_RUN_SET_REFS: RunSetTypes.Ref[] = [];

/**
 * NGW replacement for `useRegexMetrics`: keeps `config.metrics` in sync with the
 * saved regex by resolving matches server-side (RE2 via metricSearch) rather
 * than scanning the retired history-key set.
 *
 * Pass `enabled: false` (e.g. flag off) to fall back to the legacy history-key
 * path; the underlying query is also skipped when not in regex mode.
 */
export function useRegexMetricsSync(
  config: MetricsConfig,
  updateConfig: UpdateConfig,
  runSetRefs: RunSetTypes.Ref[] | undefined,
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined,
  {enabled = true}: {enabled?: boolean} = {}
) {
  const active =
    enabled && Boolean(config.useMetricRegex) && Boolean(config.metricRegex);

  const {results, loading, error, searchErrorMessage} = useMetricKeySearch(
    runSetRefs ?? EMPTY_RUN_SET_REFS,
    customRunNamesRef,
    {
      metricNamePattern: config.metricRegex || undefined,
      pageSize: REGEX_METRIC_MAX_LEN,
      skip: !active,
    }
  );

  useEffect(() => {
    if (!active || loading) {
      return;
    }
    // A failed search returns no results, which is not the same as "the regex
    // matches nothing". Leave the plotted metrics alone rather than clearing
    // the panel: for a rejected regex that means the panel keeps drawing what
    // the last accepted pattern matched while the user fixes this one.
    if (error != null || searchErrorMessage != null) {
      return;
    }
    // Exclude any metric that was ever a histogram. A histogram can only render
    // as a single-metric heatmap, so it can't be one of many regex matches; its
    // histogram points would otherwise fall through to the line renderer as
    // non-numeric NaN blobs. This mirrors the history-key regex path
    // (isHistogramKey in getSelectedMetrics), keyed off the metric-search
    // `types` set instead of typeCounts.
    const matched = results
      .filter(r => !r.types.includes('histogram'))
      .map(r => r.key);
    const currentMetrics = config.metrics ?? [];
    const matchedSet = new Set(matched);
    const changed =
      currentMetrics.length !== matched.length ||
      !currentMetrics.every(m => matchedSet.has(m));
    if (changed) {
      // isUndoable: false so automatic metric expansion doesn't trip the
      // "unsaved changes" prompt (matches legacy useRegexMetrics).
      updateConfig({metrics: matched}, {isUndoable: false});
    }
  }, [
    active,
    loading,
    error,
    searchErrorMessage,
    results,
    config.metrics,
    updateConfig,
  ]);
}
